import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Sparkles, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ImportLink } from "@/pages/Integrations";

const CHUNK = 8 * 1024 * 1024;
const inputCls = "rounded-lg bg-[#070912] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#2f7bff] w-full";

export const UploadZone = ({ onUploaded }) => {
  const { t, lang } = useI18n();
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);
  const [game, setGame] = useState("");
  const [focus, setFocus] = useState("");
  const [auto, setAuto] = useState(true);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/") && !/\.(mp4|mov|mkv|webm|avi)$/i.test(file.name)) {
      toast.error(t("up.bad_format"));
      return;
    }
    try {
      setProgress(0);
      const { data } = await api.post("/videos/upload/init", { filename: file.name, size: file.size, game });
      for (let off = 0; off < file.size; off += CHUNK) {
        await api.put(`/videos/${data.id}/chunk`, file.slice(off, off + CHUNK), { headers: { "Content-Type": "application/octet-stream" } });
        setProgress(Math.min(100, Math.round(((off + CHUNK) / file.size) * 100)));
      }
      await api.post(`/videos/${data.id}/complete`, { auto_analyze: auto, analyze: { focus, game: game || null, lang } });
      toast.success(auto ? t("up.done_ai") : t("up.done"));
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProgress(null);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="cx-card cx-brackets rounded-2xl p-5 sm:p-6 lg:p-8 relative overflow-hidden h-full flex flex-col" data-testid="upload-panel">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#a746ff]/15 blur-[90px] rounded-full pointer-events-none" />
      <div data-testid="dashboard-upload-dropzone"
        onClick={() => progress === null && input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
        className={`relative flex-1 grid place-items-center rounded-xl border-2 border-dashed px-4 sm:px-6 py-10 sm:py-14 text-center cursor-pointer transition-colors duration-200 ${drag ? "border-[#3ad7ff] bg-[#2f7bff]/10" : "border-white/10 hover:border-[#2f7bff]/60 hover:bg-[#2f7bff]/[0.04]"}`}>
        <input ref={input} type="file" accept="video/*,.mkv" className="hidden" data-testid="upload-file-input" onChange={(e) => upload(e.target.files[0])} />
        {progress === null ? (
          <div>
            <UploadCloud className="w-12 h-12 mx-auto text-[#3ad7ff]" />
            <div className="font-display text-lg sm:text-xl font-semibold mt-4 text-white">{t("up.drop")}</div>
            <div className="text-sm text-slate-400 mt-1">{t("up.formats")}</div>
          </div>
        ) : (
          <div data-testid="upload-progress" className="w-full">
            <Loader2 className="w-10 h-10 mx-auto text-[#3ad7ff] animate-spin" />
            <div className="font-mono text-sm mt-4 text-white">{t("up.progress", { p: progress })}</div>
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden max-w-sm mx-auto">
              <div className="h-full bg-gradient-to-r from-[#2f7bff] to-[#a746ff] transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>
      <div className="mt-4"><ImportLink compact onDone={onUploaded} /></div>
      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mt-3">
        <input data-testid="upload-game-input" value={game} onChange={(e) => setGame(e.target.value)} placeholder={t("up.game")} className={inputCls} />
        <input data-testid="upload-focus-input" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder={t("up.focus")} className={inputCls} />
      </div>
      <label className="flex items-start sm:items-center gap-3 mt-5 text-sm text-slate-300 cursor-pointer">
        <Switch checked={auto} onCheckedChange={setAuto} data-testid="upload-auto-analyze-switch" />
        <Sparkles className="w-4 h-4 text-[#c79bff] shrink-0 mt-0.5 sm:mt-0" /> <span>{t("up.auto")}</span>
      </label>
    </div>
  );
};
